import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, Button } from "react-bootstrap";
import MainCard from "../../component/MainCard/MainCard";
import HandleAllTracker from "./HandleAllTracker/HandleAllTracker";
import * as actions from "../../Redux/Actions";
import useLoadingIndicator from "../../component/Loader/Loader";
import SearchBar from "../../component/SearchBar/SearchBar";
import "./HRBPTracker.scss";
import { FiGrid, FiUsers, FiBriefcase, FiClock, FiAlertTriangle, FiFilter, FiPlus, FiDownload, FiCalendar } from 'react-icons/fi';
import ModalComponents from "../../component/ModalComponent/ModalComponent";
import AddTask from "./TaskDetails/AddTask";
import { exportHrbpActivitiesApi } from "../../Redux/Services/EmployeeDBServices";
import { showErrorToastMessage } from "../../component/Toasts/Toasts";
import AdvancedFilterModal, { IAdvancedFilters } from './AdvancedFilterModal';
import { Link } from "react-router-dom";
import { taskMappings } from "./TaskDetails/taskMappings";
const HR_ADMIN_ROLE = 'hr-admin';

type StatCardProps = {
  icon: React.ReactElement;
  value: string | number;
  label: string;
  iconBgColor: string;
  iconColor: string;
};

const StatCard: React.FC<StatCardProps> = ({ icon, value, label, iconBgColor, iconColor }) => {
  return (
    <div className="stat-card">
      <div className="stat-icon-wrapper" style={{ backgroundColor: iconBgColor }}>
        {React.cloneElement(icon, { color: iconColor, size: 20 })}
      </div>
      <div className="stat-text">
        <span className="stat-value">{value}</span>
        <span className="stat-label">{label}</span>
      </div>
    </div>
  );
};

// This defines the default, empty state for our advanced filters.
const initialAdvancedFilters: IAdvancedFilters = {
  dateRangeType: 'dueDate',
  startDate: null,
  endDate: null,
  hrbpIds: [],
  statuses: [],
  age: null,
  clientIds: []
};
const TABS = ["All", ...Object.keys(taskMappings)];

const HRBPTracker = () => {
  const dispatch = useDispatch();
  const { loadingComponent, showLoadingIndicator, hideLoadingIndicator } = useLoadingIndicator();

  const userRoles = localStorage.getItem("role") || "";
  const isHrAdmin = userRoles.includes(HR_ADMIN_ROLE);

  // State for all filter controls
  const [activeTab, setActiveTab] = useState("All");
  const [searchValue, setSearchValue] = useState("");
  const [advancedFilters, setAdvancedFilters] = useState<IAdvancedFilters>(initialAdvancedFilters);
  const [refreshKey, setRefreshKey] = useState(0);
  // --- State for the "My Tasks" button ---
  const [myTasksOnly, setMyTasksOnly] = useState(true);

  // State for modals
  const [showAddTask, setShowAddTask] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); 
  const [showFilterModal, setShowFilterModal] = useState(false);

  const { getHrbpSummary } = useSelector((state: any) => state.employeeReducer);

  // Fetch initial summary data on page load
  useEffect(() => {
    dispatch(actions.getHrbpSummaryAction());
    dispatch(actions.getHrbpDashboardDataAction());
  }, [dispatch]);

  const summaryData = getHrbpSummary?.data || {};

  // --- Handlers for all filter actions ---
  const handleApplyFilters = (newFilters: IAdvancedFilters) => {
    setAdvancedFilters(newFilters);
  };

  const handleClearFilters = () => {
    setAdvancedFilters(initialAdvancedFilters);
  };
  const handleCardClick = (searchTerm: string) => {
    setActiveTab("All");
    setAdvancedFilters(initialAdvancedFilters);
    setSearchValue(searchTerm);
  };

  // --- Handlers for modals ---
  const handleAddModalShow = () => setShowAddTask(true);
  const hideAddModal = () => setShowAddTask(false);

  const handleExport = async () => {
    showLoadingIndicator();

    const exportFilters = {
      ...advancedFilters,
      activityType: activeTab === "All" ? null : activeTab,
      search: searchValue,
      myTasksOnly: isHrAdmin ? (myTasksOnly ? 'true' : undefined) : 'true',
    };

    try {
      const response = await exportHrbpActivitiesApi(exportFilters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'hrbp-activity-export.csv');
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      showErrorToastMessage("Failed to export data. Please try again.");
    } finally {
      hideLoadingIndicator();
    }
  };

  return (
    <>
      {loadingComponent()}
      <MainCard>
        <div className="page-header-container mt-3 ms-4 me-4">
          <div className="heading-wrapper">
            <h2>My Planner</h2>
            <p className="subtitle">Plan, track, and execute your HR activities</p>
          </div>
          <div className="summary-stats-container ms-4 mb-4">
            <StatCard icon={<FiGrid />} value={summaryData.totalTasks || 0} label="Total Task" iconBgColor="#e7f0ff" iconColor="#0d6efd" />
            <Link to="/my-employees" className="stat-card-link">
              <StatCard icon={<FiUsers />} value={summaryData.totalEmployees || 0} label="Employees" iconBgColor="#e6f7f2" iconColor="#198754" />
            </Link>
            <Link to="/my-clients" className="stat-card-link">
              <StatCard icon={<FiBriefcase />} value={summaryData.totalClients || 0} label="Clients" iconBgColor="#f0eaff" iconColor="#6f42c1" />
            </Link>
            <StatCard icon={<FiClock />} value={`${summaryData.avgTaskAge || 0}d`} label="Avg SLA" iconBgColor="#fff8e1" iconColor="#ffc107" />
            <div className="clickable-stat-card" onClick={() => handleCardClick('overdue')}>
              <StatCard icon={<FiAlertTriangle />} value={summaryData.overdueTasks || 0} label="Overdue" iconBgColor="#fdeded" iconColor="#dc3545" />
            </div>
            <StatCard icon={<FiCalendar />} value={summaryData.upcomingTasks || 0} label="Upcoming" iconBgColor="#e3fafc" iconColor="#17a2b8" />
          </div>
        </div>

        <div className="filter-bar-container ms-4 me-4">
          <div className="filter-group">
            <SearchBar filter={searchValue} setFilter={setSearchValue} placeholder="Search tasks, employees..." onSearchClicked={() => { }} />
            <div className="activity-filter-dropdown">
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" className="filter-dropdown">
                  {activeTab}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {TABS.map(tabName => (
                    <Dropdown.Item key={tabName} onClick={() => setActiveTab(tabName)}>
                      {tabName}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <Button variant="outline-secondary" className="filter-dropdown" onClick={() => setShowFilterModal(true)}>
              <FiFilter className="me-2" /> More Filters
            </Button>
            {isHrAdmin && (
              <Button
                variant={myTasksOnly ? "dark" : "outline-secondary"}
                className="filter-dropdown"
                onClick={() => setMyTasksOnly(!myTasksOnly)}
              >
                My Tasks
              </Button>
            )}
          </div>

          <div className="table-actions-container">
            <button type="button" onClick={handleExport} className="btn btn-outline-secondary">
              <FiDownload className="me-2" /> Export
            </button>
            <button type="button" onClick={handleAddModalShow} className="btn btn-primary">
              <FiPlus className="me-2" /> Add Task
            </button>
          </div>
        </div>

        <div className="profile-containers">
          <HandleAllTracker
            activityTypeFilter={activeTab === "All" ? null : activeTab}
            searchFilter={searchValue}
            advancedFilters={advancedFilters}
            myTasksOnly={isHrAdmin ? myTasksOnly : true}
             refreshTrigger={refreshTrigger}
          />
        </div>
      </MainCard>

      <ModalComponents
        show={showAddTask}
        onHide={hideAddModal}
        title="Add Task Details"
        modalBody={
          <AddTask
            hideAddModal={hideAddModal}
            onAddSuccess={() => setRefreshTrigger(prev => prev + 1)}
          />
        }
        className="modal-lg"
      />
      <AdvancedFilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        currentFilters={advancedFilters}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />
    </>
  );
};

export default HRBPTracker;